import React, { useState, useEffect, useRef } from 'react'
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  LinearProgress,
  Stack,
  Alert,
  Chip,
  Divider
} from '@mui/material'
import {
  Download as DownloadIcon,
  CheckCircle as CheckCircleIcon,
  TableChart as TableChartIcon,
  AccessTime as TimeIcon,
  Token as TokenIcon
} from '@mui/icons-material'
import { ExtractedQuestion } from '../../types'

// Extended interface for merged questions with dictionary data
interface MergedQuestion extends ExtractedQuestion {
  data_source?: 'dictionary' | 'questionnaire' | 'merged'
  dictionary_enhanced?: boolean
  warnings?: string[]
  source_comparison?: {
    question_id: { dictionary: string | null; questionnaire: string | null }
    question_text: { dictionary: string | null; questionnaire: string | null }
    question_type: { dictionary: string | null; questionnaire: string | null }
    answer_options: { dictionary: string[] | null; questionnaire: string[] | null }
    choice_details?: { dictionary: any[] | null; questionnaire: string[] | null }
  }
}
import { config } from '../../config/environment'

interface TabPlanGenerationStepProps {
  sessionId?: string
  questions?: MergedQuestion[]
  onError?: (error: string) => void
  onGenerationComplete?: () => void
}

interface TabPlanResult {
  success: boolean
  filename: string
  downloadPath: string
  processingTime?: {
    duration: number
    durationMinutes: number
    durationSeconds: string
  }
  tokenUsage?: {
    inputTokens: number
    outputTokens: number
    totalTokens: number
  }
  metadata: {
    questionsIdentified: number
    suggestedTabs: number
    crosstabGroups: number
    keySegments: number
  }
}

export const TabPlanGenerationStep: React.FC<TabPlanGenerationStepProps> = ({
  sessionId,
  questions = [],
  onError,
  onGenerationComplete
}) => {
  const [isGenerating, setIsGenerating] = useState(true)
  const [result, setResult] = useState<TabPlanResult | null>(null)
  const hasCompletedRef = useRef(false)

  useEffect(() => {
    if (sessionId && questions.length > 0) {
      generateTabPlan()
    }
  }, [sessionId, questions])

  const generateTabPlan = async () => {
    try {
      const response = await fetch(`${config.API_BASE_URL}/api/tabplan/generate`, {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          selectedQuestions: questions.map(q => ({
            id: q.question_id,
            text: q.question_text,
            type: q.question_type,
            base: q.base,
            question_pattern: q.question_pattern,
            category: q.category,
            responseOptions: q.source_comparison?.choice_details?.dictionary 
              ? q.source_comparison.choice_details.dictionary.map((choice: any) => ({
                  text: choice.text,
                  codeValue: choice.code,
                  rowValue: choice.rowValue
                }))
              : Array.isArray(q.answer_options) 
                ? q.answer_options 
                : (q.answer_options ? [q.answer_options] : []),
            statements: q.statements || []
          })),
          sessionId
        })
      })

      const data = await response.json()
      
      if (data.success) {
        setResult(data)
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true
          onGenerationComplete?.()
        }
      } else {
        if (!hasCompletedRef.current) {
          hasCompletedRef.current = true
          onError?.(data.message || 'Generation failed')
        }
      }
    } catch (error) {
      if (!hasCompletedRef.current) {
        hasCompletedRef.current = true
        onError?.('Failed to generate Tab Plan')
      }
    } finally {
      setIsGenerating(false)
    }
  }

  const handleDownload = () => {
    if (result?.downloadPath) {
      window.open(`${config.API_BASE_URL}/${result.downloadPath}`, '_blank')
    }
  }

  if (isGenerating) {
    return (
      <Box sx={{ textAlign: 'center', py: 4 }}>
        <TableChartIcon sx={{ fontSize: 48, color: 'primary.main', mb: 2 }} />
        <Typography variant="h6" gutterBottom>Generating Tab Plan...</Typography>
        <LinearProgress sx={{ width: '300px', mx: 'auto', mb: 2 }} />
        <Typography variant="body2" color="text.secondary">
          Creating Excel file with {questions.length} questions
        </Typography>
      </Box>
    )
  }

  if (!result) {
    return (
      <Alert severity="error">
        Failed to generate Tab Plan. Please try again.
      </Alert>
    )
  }

  return (
    <Box>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <CheckCircleIcon color="success" />
        <Typography variant="h6">Tab Plan Generated Successfully</Typography>
      </Stack>

      {(result.processingTime || result.tokenUsage) && (
        <Card variant="outlined" sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>
              Processing Summary
            </Typography>
            <Grid container spacing={2}>
              {result.processingTime && (
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TimeIcon sx={{ fontSize: 20, color: 'primary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Processing Time
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {result.processingTime.durationMinutes}m {result.processingTime.durationSeconds}s
                      </Typography>
                    </Box>
                  </Stack>
                </Grid>
              )}
              {result.tokenUsage && (
                <Grid item xs={12} sm={6}>
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <TokenIcon sx={{ fontSize: 20, color: 'secondary.main' }} />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        AI Tokens Used
                      </Typography>
                      <Typography variant="h6" sx={{ fontWeight: 500 }}>
                        {result.tokenUsage.totalTokens.toLocaleString()}
                      </Typography>
                      <Stack direction="row" spacing={0.5} sx={{ mt: 0.5 }}>
                        <Chip 
                          label={`${result.tokenUsage.inputTokens.toLocaleString()} in`} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                        <Chip 
                          label={`${result.tokenUsage.outputTokens.toLocaleString()} out`} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: '20px' }}
                        />
                      </Stack>
                    </Box>
                  </Stack>
                </Grid>
              )}
            </Grid>
          </CardContent>
        </Card>
      )}

      <Card variant="outlined">
        <CardContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            File: {result.filename}
          </Typography>
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
            size="large"
          >
            Download Excel File
          </Button>
        </CardContent>
      </Card>
    </Box>
  )
}

export default TabPlanGenerationStep